import { CloseOutlined } from '@ant-design/icons'
import { Button, Checkbox, ConfigProvider, Drawer, Modal } from 'antd'
import { useEffect, useState } from 'react'

export interface PurgeDataItem {
  id: string
  label: string
  value: string
  enabled: boolean
  selected_by_default: boolean
}

interface PurgeDataSidebarProps {
  isOpen: boolean
  fields: PurgeDataItem[]
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (selectedItems: string[]) => Promise<void>
}

export default function PurgeDataSidebar({
  isOpen,
  fields,
  isSubmitting,
  onClose,
  onSubmit,
}: PurgeDataSidebarProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [confirmVisible, setConfirmVisible] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setConfirmVisible(false)
      return
    }

    setSelectedItems(fields.filter((field) => field.selected_by_default).map((field) => field.id))
    setConfirmVisible(false)
  }, [fields, isOpen])

  const handleCheckboxChange = (itemId: string, checked: boolean) => {
    setSelectedItems((current) =>
      checked ? [...current, itemId] : current.filter((id) => id !== itemId)
    )
  }

  const handleClose = () => {
    if (!isSubmitting) onClose()
  }

  const handleConfirm = async () => {
    try {
      await onSubmit(selectedItems)
      setConfirmVisible(false)
    } catch {
      // The parent mutation displays the request error; keep the modal open for retrying.
    }
  }

  return (
    <>
      <Drawer
        title={<span className="text-base font-medium text-[rgba(0,0,0,0.85)]">Purge Data</span>}
        closeIcon={<CloseOutlined />}
        placement="right"
        onClose={handleClose}
        open={isOpen}
        width={500}
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
        styles={{
          header: { padding: '16px 24px', borderBottom: '1px solid #f0f0f0' },
          body: { padding: 0 },
        }}
        footer={null}
      >
        <div className="px-6 py-4">
          <p className="mb-6 text-sm font-normal text-[rgba(0,0,0,0.65)]">
            Purging irreversibly replaces selected attendee personal data with masked values
            (&apos;**&apos;). Full name is always included.
          </p>

          <div className="rounded-sm bg-[#f9fafb] p-6">
            <h4 className="mb-6 text-base font-medium text-[rgba(0,0,0,0.85)]">
              Attendee Personal Data
            </h4>

            <ConfigProvider theme={{ components: { Checkbox: { colorPrimary: '#0e4da4' } } }}>
              <div className="space-y-5">
                {fields.map((field) => (
                  <div key={field.id} className="flex items-center">
                    <div className="w-[160px] text-sm font-normal text-[rgba(0,0,0,0.45)]">
                      {field.label}
                    </div>
                    <div className="flex-1 text-sm font-normal text-[rgba(0,0,0,0.85)]">
                      {field.value}
                    </div>
                    <Checkbox
                      checked={selectedItems.includes(field.id)}
                      disabled={!field.enabled || isSubmitting}
                      onChange={(event) => handleCheckboxChange(field.id, event.target.checked)}
                      className="ml-4"
                      style={{ transform: 'scale(1.2)' }}
                    />
                  </div>
                ))}
              </div>
            </ConfigProvider>
          </div>

          <div className="mt-6">
            <Button
              type="primary"
              onClick={() => setConfirmVisible(true)}
              disabled={selectedItems.length === 0}
              loading={isSubmitting}
              className="h-10 rounded-sm border-[#132b51] bg-[#132b51] px-6 hover:border-[#0f1f3c] hover:bg-[#0f1f3c]"
            >
              Purge Data
            </Button>
          </div>
        </div>
      </Drawer>

      <Modal
        title="Confirm data purge"
        open={confirmVisible}
        onCancel={() => !isSubmitting && setConfirmVisible(false)}
        onOk={handleConfirm}
        okText="Purge Data"
        cancelText="Cancel"
        confirmLoading={isSubmitting}
        maskClosable={!isSubmitting}
        closable={!isSubmitting}
      >
        <p>
          The selected values, including full name, will be replaced with &apos;**&apos; for every
          attendee. The original data cannot be recovered.
        </p>
      </Modal>
    </>
  )
}
